import React from 'react';
import { Stack } from '@mui/material';

import { PerpendicularLine } from '@paragon/contract-library';
import { DistanceField } from '@paragon/material';

import { findAndSetReferenceLine } from '../../../../store/actions/editorActions';
import { startSetReferenceLineGuid, startSetReferenceLineIntersection } from '../../../../store/actions/insertActions';
import { getAsyncRequestForActiveComponentDesign } from '../../../../store/selectors/getAsyncRequestForActiveComponentDesignSelector';
import { getReferenceLineLongNamesByGuidForActiveComponent } from '../../../../store/selectors/getReferenceLineLongNamesByGuidForActiveComponentSelector';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import ReferenceLineIntersectionProperty from '../elements/ReferenceLineIntersectionProperty';
import ReferenceLineNameProperty from '../elements/ReferenceLineNameProperty';
import ReferenceLineProperty from '../elements/ReferenceLineProperty';
import { ReferenceLineTypeProperty } from '../elements/ReferenceLineTypeProperty';

interface Props {
  line: PerpendicularLine;
  afterUpdateLine?: () => void;
}

const PerpendicularLineProperties = ({ line, afterUpdateLine }: Props) => {
  const dispatch = useAppDispatch();
  const referenceLineLongNamesByGuid = useAppSelector(getReferenceLineLongNamesByGuidForActiveComponent);
  const disabled = useAppSelector((state) => {
    const asyncRequest = getAsyncRequestForActiveComponentDesign(state);
    return propertiesShouldBeViewOnly(state) || (!!asyncRequest && asyncRequest.type !== 'updateFeatures');
  });

  const onApplyOffset = async (newOffset: number) => {
    const newLine = { ...line, offset: newOffset };
    await dispatch(findAndSetReferenceLine(newLine));
    afterUpdateLine && afterUpdateLine();
  };

  if (!referenceLineLongNamesByGuid) {
    throw new Error(
      'Rendering PerpendicularLineSubPanel but active component design reference line names are missing.',
    );
  }

  return (
    <Stack direction="column" spacing={2}>
      <ReferenceLineNameProperty sx={{ marginBottom: '0!important' }} line={line} disabled={disabled} />
      <ReferenceLineTypeProperty line={line} disabled={disabled} />

      <ReferenceLineProperty
        referenceLineLongNamesByGuid={referenceLineLongNamesByGuid}
        line={line}
        disabled={disabled}
        lineKey="lineGuid"
        propertyTitle="Perpendicular To"
        startSetReferenceLineGuid={(referenceLine, key) => dispatch(startSetReferenceLineGuid(referenceLine, key))}
      />

      <ReferenceLineIntersectionProperty
        referenceLineLongNamesByGuid={referenceLineLongNamesByGuid}
        disabled={disabled}
        line={line}
        lineKeys={{ key1: 'basePointLine1Guid', key2: 'basePointLine2Guid' }}
        property="Intersection"
        onStartSelectingIntersection={(referenceLine, key1, key2) =>
          dispatch(startSetReferenceLineIntersection(referenceLine, key1, key2))
        }
      />

      <DistanceField value={line.offset} label="Offset" onSubmit={onApplyOffset} disabled={disabled} />
    </Stack>
  );
};

export default PerpendicularLineProperties;
